import { useCallback, useMemo } from "react"

import { useEditor, useNode } from "@craftjs/core"
import { get, omit } from "lodash"

import { SHARED_KEYS } from "../../utils"
import { useBlockDefinition } from "../../utils/definition"
import { useStylePatterns } from "../../utils/tailwindConfig/hooks"

export function useStylePatternOptions() {
  const { value: patterns } = useStylePatterns()

  return useMemo(() => Object.keys(patterns || {}).map(key => ({
    label: key,
    value: key,
  })), [patterns])
}

export function useStylePatternUpdater(enabledUpdate, pattern) {
  const { value: patternData = {}, setValue: updatePatterns } = useStylePatterns(pattern)
  const { id, selected } = useNode(({ id, events: { selected } }) => ({
    id,
    selected,
  }))

  const { query } = useEditor()
  const { getDefinition } = useBlockDefinition()

  const getStyleProperties = useCallback(name => {
    const freshProps = query.node(id).get().data.props.properties

    if (name) {
      return get(freshProps, name)
    }

    const definition = getDefinition(id) || {}
    const ownPropNames = definition.props?.filter(p => p.own).map(p => p.name) ?? []
    const properties = omit(freshProps, ['stylePattern', 'attributes', 'parsedClassName', ...ownPropNames])

    if (SHARED_KEYS.includes(pattern)) {
      properties.parsedClassName = freshProps.attributes?.className
    }
    return properties
  }, [id, pattern, getDefinition, query])

  const create = useCallback(pattern => {
    if (!selected || !pattern) return
    updatePatterns({ ...patternData, [pattern]: getStyleProperties() })
  }, [selected, patternData, updatePatterns, getStyleProperties])

  const update = useCallback(name => {
    if (!selected || !pattern || JSON.stringify(pattern) === '{}') return
    const value = getStyleProperties(name)
    updatePatterns(value)
  }, [selected, pattern, updatePatterns, getStyleProperties])

  return useMemo(() => ({
    create,
    update,
  }), [create, update])
}
